import React from 'react';

import { FormikValues } from 'formik';

import {
  adminActivatePharmacist,
  adminShowPharmacist,
  HelpTypes,
  IAdminPharmacistDetailsSearchData,
  IAdminPharmacistListBaseData,
  ProfessionalOnlyUsers,
} from '@pharmaplan/common';

import {
  activePharmacistListHeaders,
  activePharmacistListRows,
  deactivatedPharmacistListRows,
  deactivePharmacistListHeaders,
  deletedPharmacistListHeaders,
  deletedPharmacistListRows,
  hiddenPharmacistListHeaders,
  hiddenPharmacistListRows,
  pharmacistSearchedDetailHeaders,
  searchedDetailsRow,
} from '../../components/Admin/PharmacistList/helper';
import { setDialog } from '../../reducers/dialogReducer';
import {
  DeactivateModalIcon,
  UserListTabTypes,
  ScreenTypes,
  nameLabels,
} from '../../helpers/Constants';

import useUserProfileLink from '../Admin/useUserProfileLink';
import { useAppDispatch } from '../useAppDispatch';
import usePharmacistList from './usePharmacistList';
import DeactivateDialog from '../../components/Admin/PharmacistList/DeactivateDialog';
import strings from '../../localization';
import { renderScreen } from '../../actions/drawerActions';
import useDrawer from '../useDrawer';
import useProfessionalListConfig from './useProfessionalListConfig';

const { active, deactivated, hidden, deleted, detailsSearch } = UserListTabTypes;

const usePharmacistListTabs = (
  selectedTab: UserListTabTypes,
  userType: ProfessionalOnlyUsers,
  apiUserType = HelpTypes.pharmacist,
) => {
  const dispatch = useAppDispatch();

  const { actions } = usePharmacistList(apiUserType);
  const { goToUserProfile } = useUserProfileLink();
  const { openDrawer } = useDrawer();
  const { api, dataConfig, successes } = useProfessionalListConfig(userType);

  const { data } = dataConfig[selectedTab as keyof typeof dataConfig];

  const activatePharmacist = (values: FormikValues) => {
    const { id, reason } = values;
    dispatch(
      adminActivatePharmacist({
        pharmacistId: id,
        reason,
      }),
    );
  };

  const unhidePharmacist = (values: FormikValues) => {
    const { id, reason } = values ?? {};
    dispatch(adminShowPharmacist({ pharmacistId: id, reason }));
  };

  const activateDialog = (pharmacistId: string) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <DeactivateDialog
              id={pharmacistId}
              iconType={DeactivateModalIcon.positiveConfirmation}
              confirmationMessage={strings.areYouSureActivatePharmacist}
              heading={
              strings.formatString(
                strings.activateFormat,
                nameLabels().roleLabel,
              ) as string
            }
              onSubmit={activatePharmacist}
            />
          ),
          heading: {
            title: '',
          },
          showCloseButton: true,
        }),
      );
    };

  const showPharmacistDialog = (pharmacistId: string) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <DeactivateDialog
              id={pharmacistId}
              iconType={DeactivateModalIcon.positiveConfirmation}
              confirmationMessage={strings.areYouSureUnhidePharmacist}
              heading={
              strings.formatString(
                strings.unhideFormat,
                nameLabels().roleLabel,
              ) as string
            }
              onSubmit={unhidePharmacist}
            />
          ),
          heading: {
            title: '',
          },
          showCloseButton: true,
        }),
      );
    };

  const goToWatchlists = (pharmacistId: string) =>
    () => {
      openDrawer();
      dispatch(
        renderScreen({
          screenNumber: 1,
          screenType: ScreenTypes.adminWatchlists,
          pharmacistId,
        }),
      );
    };

  const rowConfig = {
    [active]: activePharmacistListRows(
      data as Array<IAdminPharmacistListBaseData>,
      goToUserProfile,
      goToWatchlists,
      apiUserType,
      actions,
    ),
    [deactivated]: deactivatedPharmacistListRows(
      data as Array<IAdminPharmacistListBaseData>,
      goToUserProfile,
      activateDialog,
    ),
    [hidden]: hiddenPharmacistListRows(
      data as Array<IAdminPharmacistListBaseData>,
      goToUserProfile,
      showPharmacistDialog,
    ),
    [deleted]: deletedPharmacistListRows(
      data as Array<IAdminPharmacistListBaseData>,
      goToUserProfile,
    ),
    [detailsSearch]: searchedDetailsRow(
      data as Array<IAdminPharmacistDetailsSearchData>,
      goToUserProfile,
    ),
  };

  const headersConfig = {
    [active]: activePharmacistListHeaders(apiUserType),
    [deactivated]: deactivePharmacistListHeaders(),
    [hidden]: hiddenPharmacistListHeaders(),
    [deleted]: deletedPharmacistListHeaders(),
    [detailsSearch]: pharmacistSearchedDetailHeaders(),
  };

  return {
    rows: rowConfig[selectedTab],
    headers: headersConfig[selectedTab],
    api: api[selectedTab as keyof typeof api],
    data: dataConfig[selectedTab],
    loadSuccess: successes[selectedTab],
  };
};

export default usePharmacistListTabs;
